<template>
  <div>
    <d-tabs class="custom-tabs">
      <!-- Aba de Índices de Risco -->
      <d-tab
        title="Índices de Risco"
        :active="activeTab === 'risks'"
        @click="activeTab = 'risks'"
      >
        <div class="table-responsive">
          <table class="table w-100">
            <thead class="bg-grey">
              <tr>
                <th scope="col" class="td-1">Índice de Risco</th>
                <th scope="col" class="td-2">Conclusão</th>
                <th scope="col" class="td-3">Cor</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(risk, riskKey) in riskRows" :key="riskKey">
                <!-- Índice de Risco -->
                <td class="td">
                  <div class="criteria-container">
                    <!-- Risco baixo (risk1) -->
                    <template v-if="risk.type === 'max-only'">
                      <span>valor &lt;</span>
                      <d-form-input
                        type="text"
                        v-model="model.risks[riskKey].max"
                        :placeholder="risk.placeholder"
                        class="value-input"
                      />
                    </template>

                    <!-- Risco com min e max (risk2, risk3) -->
                    <template v-else-if="risk.type === 'range'">
                      <div class="range-group">
                        <span>valor &gt;=</span>
                        <d-form-input
                          type="text"
                          v-model="model.risks[riskKey].min"
                          :placeholder="`Início ${risk.placeholder}`"
                          class="value-input"
                        />
                      </div>
                      <div class="range-group">
                        <span>e valor &lt;=</span>
                        <d-form-input
                          type="text"
                          v-model="model.risks[riskKey].max"
                          :placeholder="`Fim ${risk.placeholder}`"
                          class="value-input"
                        />
                      </div>
                    </template>

                    <!-- Risco alto (risk4) -->
                    <template v-else-if="risk.type === 'min-only'">
                      <span>valor &gt;</span>
                      <d-form-input
                        type="text"
                        v-model="model.risks[riskKey].min"
                        :placeholder="risk.placeholder"
                        class="value-input"
                      />
                    </template>
                  </div>
                </td>

                <!-- Conclusão -->
                <td class="td">
                  <d-form-input
                    type="text"
                    class="conclusion-input"
                    v-model="model.risks[riskKey].conclusion"
                    :placeholder="`Conclusão ${risk.placeholder}`"
                  />
                </td>

                <!-- Cor -->
                <td class="td pr-3">
                  <d-input
                    type="color"
                    class="color-picker"
                    v-model="model.risks[riskKey].color"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </d-tab>

      <!-- Aba de LQ -->
      <d-tab title="LQ" :active="activeTab === 'LQ'" @click="activeTab = 'LQ'">
        <div class="table-responsive">
          <table class="table w-100">
            <thead class="bg-grey">
              <tr>
                <th scope="col" class="td-1">Critério</th>
                <th scope="col" class="td-2">Conclusão</th>
                <th scope="col" class="td-3">Cor</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="model">
                <!-- Critério -->
                <td class="td"><span class="pl-1">LQ = Abaixo</span></td>
                <!-- Conclusão -->
                <td class="td">
                  <d-form-input
                    type="text"
                    class="conclusion-input"
                    v-model="model.lq.conclusion"
                    placeholder="Conclusão quando LQ = Abaixo"
                  />
                </td>
                <!-- Cor -->
                <td class="td">
                  <d-input
                    type="color"
                    class="color-picker"
                    v-model="model.lq.color"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </d-tab>

      <!-- Aba de Opções -->
      <d-tab
        v-for="(field, index) in optionFields" :key="index"
        :title="field.label"
        :active="activeTab === field.label"
        @click="activeTab = field.label"
        class="p-3"
      >
        <dynamic-list
          :list="model.options[field.model]"
          :label="field.label"
          :on-add="() => addRow(model.options[field.model])"
          :on-remove="(index) => removeRow(model.options[field.model], index)"
          :on-change="(index, row) => capitalizeInput(model.options[field.model], index)"
        />
        <d-form-row class="m-0">
          <d-button type="submit" class="btn-primary mt-2">Salvar</d-button>
        </d-form-row>
      </d-tab>
    </d-tabs>
  </div>
</template>

<script>
import Events from '_utils/events';
import { capitalize } from '_utils';
import DynamicList from '../DynamicList.vue';
import { _LOAD_MODEL_RESULTS_, DEFAULT_OPTIONS, OPTION, OPTION_FIELDS, RISK_ROWS } from '../SettingsModel';

export default {
  name: 'chemical-result-table',
  props: {
    chemical: {
      type: Object,
      required: true,
    },
  },
  components: {
    DynamicList,
  },
  data() {
    return {
      activeTab: 'risks',
      model: { ...this.chemical, ...RISK_ROWS },
      riskRows: RISK_ROWS,
      optionFields: OPTION_FIELDS,
    };
  },
  methods: {
    addRow(field) {
      field.push({ ...OPTION });
    },
    removeRow(field, indexToRemove) {
      if (indexToRemove > -1 && indexToRemove < field.length) {
        field.splice(indexToRemove, 1);
      }
    },
    capitalizeInput(field, index) {
      field[index].name = capitalize(field[index].name);
    },
  },
  mounted() {
    Events.$on(_LOAD_MODEL_RESULTS_, newValue => {
      this.model = {
        ...newValue.chemical,
        options: {
          ...DEFAULT_OPTIONS,
          ...(newValue.chemical.options || {}),
        },
      };
    });
  },
  beforeDestroy() {
    Events.$off(_LOAD_MODEL_RESULTS_);
  },
  watch: {
    model: {
      handler(newValue) {
        this.$emit('update:chemical', newValue);
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~_scss/overrides";

.td {
  vertical-align: middle;
  &-1 {
    width: 35%;
  }
  &-2 {
    width: 45%;
  }
  &-3 {
    width: 10%;
  }
}
.criteria-container {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding-left: 4px;
  span {
    white-space: nowrap;
  }
}
.range-group {
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  flex-shrink: 0;
}
.value-input {
  width: 80px;
  min-width: 48px;
  flex-shrink: 0;
}
.conclusion-input {
  width: 100%;
  min-width: 150px;
  max-width: 350px;
}
.color-picker {
  height: 37px;
  min-width: 45px;
  border-radius: 4px;
}
</style>
